/** Wildly Important Goals — the lanes, one team at a time.
 *
 * Kept apart from the ordinary project screens on purpose (Brian, 2026-09-29): projects run
 * as WIGs show a badge and a small card elsewhere, and everything about the discipline —
 * the measures, the tallies, the commitments — lives here. The team filter is the same
 * lens as the rest of the app, and the layout is the same for every team.
 */

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Mail, MonitorPlay, Target, X } from 'lucide-react'

import { EmptyState, ErrorState, Loading } from '@/components/ui'
import { useTeams } from '@/features/teams/useTeams'
import { RetireDialog, WigLaneCard } from '@/features/wig/WigLane'
import { WigSession } from '@/features/wig/WigSession'
import { WigWizard } from '@/features/wig/WigWizard'
import { useRetireWig, useWigDigestPreview, useWigs, type WigLane } from '@/features/wig/useWig'

const CHIP = 'rounded-pill border px-3 py-1 text-sm font-semibold transition'

export function WigPage() {
  const [params, setParams] = useSearchParams()
  const team = params.get('team') ?? undefined
  const { data: teamData } = useTeams()
  const { data, isPending, error } = useWigs(team)
  const retire = useRetireWig()

  const [sessionId, setSessionId] = useState<string | null>(null)
  const [editing, setEditing] = useState<WigLane | null>(null)
  const [retiring, setRetiring] = useState<WigLane | null>(null)
  const [preview, setPreview] = useState(false)

  const lanes = data?.lanes ?? []
  // Read from the live query each render, so a session shows numbers as they are recorded.
  const sessionLane = lanes.find((lane) => lane.id === sessionId) ?? null
  const teams = (teamData?.teams ?? []).filter((t) => t.team !== 'unassigned')

  const setTeam = (next?: string) => {
    const nextParams = new URLSearchParams(params)
    if (next) nextParams.set('team', next)
    else nextParams.delete('team')
    setParams(nextParams, { replace: true })
  }

  const chipStyle = (active: boolean) =>
    active
      ? { backgroundColor: 'var(--brand)', color: 'var(--brand-on)', borderColor: 'var(--brand)' }
      : { borderColor: 'var(--border)', color: 'var(--fg)' }

  return (
    <div className="mx-auto max-w-[1600px] pt-2">
      <header className="mb-5 flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 font-heading text-3xl text-fg">
            <Target size={26} aria-hidden style={{ color: 'var(--brand)' }} />
            Wildly Important Goals
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-fg-muted">
            The few projects run on the 4 Disciplines of Execution: one goal each, a result
            measured against pace, weekly lead measures the team controls, and commitments
            accounted for every week. Everything else stays in the whirlwind.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPreview(true)}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-semibold text-fg hover:bg-canvas"
          title="The WIG scoreboard section of Monday's digest, as it would be sent now"
        >
          <Mail size={15} aria-hidden />
          Monday's email
        </button>
        <Link
          to={`/wig/board${team ? `?team=${team}` : ''}`}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-semibold text-fg hover:bg-canvas"
        >
          <MonitorPlay size={15} aria-hidden />
          Friday board
        </Link>
      </header>

      <div className="mb-5 flex flex-wrap items-center gap-2" role="group" aria-label="Team">
        <button type="button" className={CHIP} style={chipStyle(!team)} aria-pressed={!team} onClick={() => setTeam()}>
          All teams
        </button>
        {teams.map((t) => (
          <button
            key={t.team}
            type="button"
            className={CHIP}
            style={chipStyle(team === t.team)}
            aria-pressed={team === t.team}
            onClick={() => setTeam(t.team)}
          >
            {t.shortLabel}
          </button>
        ))}
      </div>

      {isPending ? (
        <Loading label="Loading the WIGs" />
      ) : error ? (
        <ErrorState error={error} />
      ) : lanes.length === 0 ? (
        <EmptyState title={team ? 'No WIGs for this team yet' : 'No WIGs yet'}>
          A WIG is set up from an existing project — nothing is recreated. Open the project
          on the Projects page and press <b>Make this a WIG</b>; the wizard fills in what the
          project already knows.
          {data?.canDefine ? null : ' A Portfolio Manager or an Administrator sets them up.'}
        </EmptyState>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 2xl:grid-cols-4">
          {lanes.map((lane, index) => (
            <WigLaneCard
              key={lane.id}
              lane={lane}
              number={index + 1}
              onSession={() => setSessionId(lane.id)}
              onEdit={() => setEditing(lane)}
              onRetire={() => setRetiring(lane)}
            />
          ))}
        </div>
      )}

      {sessionLane ? <WigSession lane={sessionLane} onClose={() => setSessionId(null)} /> : null}
      {editing ? (
        <WigWizard projectId={editing.projectId} existing={editing} onClose={() => setEditing(null)} />
      ) : null}
      {retiring ? <RetireDialog lane={retiring} retire={retire} onClose={() => setRetiring(null)} /> : null}
      {preview ? <DigestPreview onClose={() => setPreview(false)} /> : null}
    </div>
  )
}

/**
 * What Monday's email will say, before Monday. Rendered in a sandboxed frame with no
 * scripts — it is email HTML, and it is shown exactly as a mail client would show it.
 */
function DigestPreview({ onClose }: { onClose: () => void }) {
  const { data, isPending, error } = useWigDigestPreview(true)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-canvas shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Monday's WIG scoreboard email"
      >
        <div className="flex items-center gap-3 border-b border-border px-5 py-3">
          <div className="flex-1">
            <h2 className="font-heading text-xl text-fg">Monday&apos;s WIG scoreboard</h2>
            <p className="text-xs text-fg-muted">
              Leads every weekly digest, and goes to everyone on a lane
              {data ? ` (${data.recipientCount} people)` : ''} even without a subscription.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-fg-muted hover:bg-surface" aria-label="Close">
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className="flex-1 p-4">
          {isPending ? (
            <Loading label="Composing the email" />
          ) : error ? (
            <ErrorState error={error} />
          ) : data?.html ? (
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={`<body style="font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#222;margin:16px">${data.html}</body>`}
              className="h-full w-full rounded-md border border-border bg-white"
            />
          ) : (
            <p className="text-sm text-fg-muted">No WIGs yet, so Monday&apos;s digest has no scoreboard section.</p>
          )}
        </div>
      </div>
    </div>
  )
}
